/**
 * M3 WORLD B2/B5: client signal manager. Owns the live set of ~3-6 SignalSources
 * around the player, re-seeded off the player's QUANTIZED position (signal.ts) so
 * the field is stable as you fly and re-derivable on load. Decays + culls signals
 * each frame; on a supercruise DROP it resolves the signal's contents and injects
 * hostiles/victims into the existing Sim.npcs + Sim.hp (tagged enc-* vs persistent
 * npc-*) so combat.ts / target.ts / npcs.ts work UNCHANGED, and surfaces the
 * scoopable salvage. The story signal (B7) is always present near Mars.
 *
 * Pure sim logic lives in sim-core/signal.ts; this manager is the stateful client
 * glue that mutates the Sim object — no Three.js here.
 */
import {
  makeSignals,
  makeStorySignal,
  forceKind,
  type SignalKind,
  stepSignalDecay,
  signalExpired,
  resolveContents,
  resolveWaves,
  nearestSignal,
  positionSeed,
  makeCombatant,
  shipById,
  identity,
  type SignalSource,
  type SignalContents,
  type SalvageNode,
  type EncounterShip,
  type NpcShip,
  type Combatant,
  type Vec3,
} from '@blackmarble/sim-core';

/** How many ambient signals to keep seeded around the player. */
const TARGET_SIGNAL_COUNT = 5;
/** Re-seed the ambient field when the player crosses this far from the last anchor. */
const RESEED_DISTANCE_KM = 100_000;

/** The live, scooped/active state for a dropped-in encounter. */
export interface ActiveEncounter {
  signal: SignalSource;
  contents: SignalContents;
  salvage: SalvageNode[]; // remaining (un-scooped) canisters
  /** Escalating-wave cursor: index of the NEXT wave yet to be spawned (0 = wave 1 pending). */
  waveIndex: number;
  /** Pre-computed wave list (resolveWaves output); empty for non-combat signals. */
  waves: EncounterShip[][];
}

export class SignalManager {
  /** All ambient + story signals currently tracked. */
  signals: SignalSource[] = [];
  /** The encounter the player is currently dropped into (null = in open flight). */
  active: ActiveEncounter | null = null;
  /** Signal ids the player has ever dropped into (persisted in the save). */
  readonly discovered = new Set<string>();
  /** CAMPAIGN W1: scripted signals owned by the campaign director. */
  private scripted: SignalSource[] = [];
  /** CAMPAIGN W1: when the campaign provides its own cenotaph signal, the
   * legacy Mars-anchored story signal is suppressed (fallback stays intact). */
  suppressLegacyStory = false;

  private lastAnchor: Vec3 | null = null;
  private lastBias: Vec3 | undefined = undefined; // B5: latest player heading
  private readonly storyPos: Vec3;

  /**
   * @param marsPos Mars's world position, anchoring the B7 story signal.
   * @param forceFirstKind AAA-13 debug: stamp ambient[0] to this kind (?signal=).
   */
  constructor(
    marsPos: Vec3,
    discoveredIds: readonly string[] = [],
    private readonly forceFirstKind?: SignalKind,
  ) {
    this.storyPos = marsPos;
    for (const id of discoveredIds) this.discovered.add(id);
    this.reseed(marsPos);
  }

  /**
   * Re-derive the ambient field around `pos` (quantized seeding) + the story signal.
   * Uses the stored player heading (lastBias) to weight distress/ambush spawns (B5).
   */
  private reseed(pos: Vec3): void {
    const ambient = makeSignals(pos, TARGET_SIGNAL_COUNT, this.lastBias).map((s) => ({
      ...s,
      discovered: this.discovered.has(s.id),
    }));
    // AAA-13 debug: force the nearest ambient signal to a chosen kind so the verify
    // beat / combat pocket are reachable on demand (a few seconds of supercruise).
    if (this.forceFirstKind && ambient[0]) ambient[0] = forceKind(ambient[0], this.forceFirstKind);
    // CAMPAIGN W1: scripted signals first, then the legacy story signal (unless
    // the campaign supplies its own cenotaph), then the ambient field.
    const story = this.suppressLegacyStory
      ? []
      : [{ ...makeStorySignal(this.storyPos), discovered: this.discovered.has('story-cenotaph-vigil') }];
    this.signals = [...this.scripted, ...story, ...ambient];
    this.lastAnchor = pos;
  }

  /** CAMPAIGN W1: add (or replace by id) a scripted signal — visible at once. */
  addScripted(sig: SignalSource): void {
    const entry = { ...sig, discovered: this.discovered.has(sig.id) };
    this.scripted = [...this.scripted.filter((x) => x.id !== sig.id), entry];
    const nonScripted = this.signals.filter((x) => !this.scripted.some((c) => c.id === x.id));
    this.signals = [...this.scripted, ...nonScripted];
  }

  /** CAMPAIGN W1: remove a scripted signal (no-op while it hosts the active encounter). */
  removeScripted(id: string): void {
    if (this.active?.signal.id === id) return;
    this.scripted = this.scripted.filter((x) => x.id !== id);
    this.signals = this.signals.filter((x) => x.id !== id);
  }

  /**
   * Per-frame tick: re-seed if the player has wandered far (new quantized cell),
   * then decay + cull ambient signals. The story signal never decays. `biasDir`
   * (player heading) weights distress/ambush toward where they're flying (B5).
   */
  update(shipPos: Vec3, dt: number, biasDir?: Vec3): void {
    this.lastBias = biasDir;
    if (this.shouldReseed(shipPos)) this.reseed(shipPos);
    this.signals = this.signals
      .map((s) => stepSignalDecay(s, dt))
      .filter((s) => !signalExpired(s));
  }

  /** True once the player has left the seeding cell of the last anchor. */
  private shouldReseed(pos: Vec3): boolean {
    if (!this.lastAnchor) return true;
    if (this.active) return false; // never reshuffle while dropped in
    const dx = pos.x - this.lastAnchor.x;
    const dy = pos.y - this.lastAnchor.y;
    const dz = pos.z - this.lastAnchor.z;
    if (Math.hypot(dx, dy, dz) < RESEED_DISTANCE_KM) return false;
    return positionSeed(pos) !== positionSeed(this.lastAnchor);
  }

  /** Nearest live signal to the ship (for the DROP prompt). */
  nearest(shipPos: Vec3): { signal: SignalSource; distanceKm: number } | null {
    return nearestSignal(this.signals, shipPos);
  }

  /**
   * Drop into `signal`: resolve its contents, inject WAVE-1 enc-* ships (plus all
   * victims) into the Sim's npcs + hp maps (so combat works unchanged), and mark it
   * discovered. Returns the ActiveEncounter with wave state so the caller can drive
   * subsequent wave spawns via checkWaveAdvance(). Idempotent: ignores a second drop.
   */
  dropInto(
    signal: SignalSource,
    npcs: NpcShip[],
    hp: Map<string, Combatant>,
  ): ActiveEncounter {
    const contents = resolveContents(signal);
    const waves = resolveWaves(signal);
    // Wave 1 hostiles — only the first wave spawns on drop-in.
    const wave1 = waves[0] ?? [];
    for (const e of [...wave1, ...contents.victims]) {
      npcs.push(this.toNpc(e));
      const sp = shipById(e.shipType);
      hp.set(e.id, makeCombatant(sp.hullMax, sp.shieldMax));
    }
    signal.discovered = true;
    this.discovered.add(signal.id);
    const enc: ActiveEncounter = {
      signal,
      contents,
      salvage: [...contents.salvage, ...contents.mineNodes],
      waves,
      waveIndex: 1, // wave 0 already spawned; next to spawn is index 1
    };
    this.active = enc;
    return enc;
  }

  /**
   * Check whether the next wave should spawn. Call once per frame while an encounter
   * is active. Spawns the next wave when living enc-* hostile count drops to ≤1 AND
   * there are still waves remaining. Injects new ships into npcs + hp exactly the
   * same way dropInto does for wave 1, and advances the waveIndex cursor.
   *
   * Returns the newly spawned EncounterShip[] (empty when no wave spawned this frame),
   * so the caller (main.ts) can hand off ids to npcRenderer.add().
   */
  checkWaveAdvance(npcs: NpcShip[], hp: Map<string, Combatant>): EncounterShip[] {
    const enc = this.active;
    if (!enc || enc.waveIndex >= enc.waves.length) return [];
    // Count living enc-* hostiles (victims don't count toward the trigger threshold).
    const liveHostiles = npcs.filter(
      (n) => n.id.startsWith('enc-') && enc.contents.victims.every((v) => v.id !== n.id),
    ).length;
    if (liveHostiles > 1) return []; // threshold: ≤1 living hostile triggers next wave
    const nextWave = enc.waves[enc.waveIndex];
    if (!nextWave || nextWave.length === 0) return [];
    enc.waveIndex++;
    for (const e of nextWave) {
      npcs.push(this.toNpc(e));
      const sp = shipById(e.shipType);
      hp.set(e.id, makeCombatant(sp.hullMax, sp.shieldMax));
    }
    return nextWave;
  }

  /** Convert an EncounterShip into an NpcShip the kinematic loop understands. */
  private toNpc(e: EncounterShip): NpcShip {
    return {
      id: e.id, // 'enc-*'
      shipType: e.shipType,
      pos: { ...e.pos },
      vel: { x: 0, y: 0, z: 0 },
      orientation: identity(), // identity until physics bodies spin up on first step
      originId: 'high-guard',
      destId: 'high-guard', // encounter ships loiter; the client gives them no lane
      laneT: 0,
      phase: 'CRUISING',
    };
  }

  /** Leave the current encounter: despawn every enc-* ship from npcs + hp. */
  clearEncounter(npcs: NpcShip[], hp: Map<string, Combatant>): NpcShip[] {
    for (const id of [...hp.keys()]) if (id.startsWith('enc-')) hp.delete(id);
    this.active = null;
    return npcs.filter((n) => !n.id.startsWith('enc-'));
  }

  /** Remove a scooped salvage node from the active encounter; returns its qty. */
  scoop(nodeId: string): { commodityId: string; qty: number } | null {
    if (!this.active) return null;
    const idx = this.active.salvage.findIndex((n) => n.id === nodeId);
    if (idx < 0) return null;
    const node = this.active.salvage[idx]!;
    this.active.salvage.splice(idx, 1);
    return { commodityId: node.commodityId, qty: node.qty };
  }

  /** Nearest un-scooped salvage node to the ship within `rangeKm` (for the SCOOP key). */
  nearestSalvage(shipPos: Vec3, rangeKm: number): SalvageNode | null {
    if (!this.active) return null;
    let best: SalvageNode | null = null;
    let bestD = rangeKm;
    for (const n of this.active.salvage) {
      const d = Math.hypot(n.pos.x - shipPos.x, n.pos.y - shipPos.y, n.pos.z - shipPos.z);
      if (d <= bestD) {
        best = n;
        bestD = d;
      }
    }
    return best;
  }
}
